import { ComponentType, MouseEvent, ReactNode, useCallback, useId, useMemo, useState } from "react";

import { Checkbox, CheckboxValue } from "capitalrx-components-2.0/lib/forms/Checkbox";
import { Shelf, Stack, StackProps } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold } from "capitalrx-components-2.0/lib/typography";
import classNames from "classnames";

import { BaseProps } from "../../BaseProps";
import { createComponentStyleHook } from "../../tokens";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { Icon } from "../Icon";
import { Panel, usePanelLevel } from "../Panel";

import legacyStyles from "./legacy/styles.module.scss";
import tokenStyles from "./styles.module.css";

/** The caret type. */
export type CaretType = "icon" | "checkbox";

/** The caret alignment. */
export type CaretAlignment = "left" | "right";

/** The set of available collapse appearances. */
export type CollapseAppearance = "contained" | "underlined" | "underlined-no-indent" | "blank" | "blank-no-indent" | "checkbox";

/** A toggle expand event handler function. */
export interface ToggleExpandHandler {
  (expanded: boolean, previous: boolean): void;
}

/**
 * Uses simple in-memory collapse state management, providing the expansion state and expand state toggle handler.
 */
export function useSimpleCollapseState(initialExpanded?: boolean): [boolean | undefined, ToggleExpandHandler] {
  const [expanded, setExpanded] = useState<boolean | undefined>(initialExpanded);

  const expandToggleHandler = useCallback<ToggleExpandHandler>((next) => {
    setExpanded(next);
  }, []);

  return [expanded, expandToggleHandler];
}

/** The `Collapse` header component properties. */
export interface CollapseHeaderProps {
  /** The visible header label/content for the collapse trigger. */
  readonly title: ReactNode;

  /** Whether the collapse content is currently expanded. */
  readonly expanded: boolean;

  /** Which side to render the caret icon on. */
  readonly alignCaret: CaretAlignment;

  /** Called when the header toggles expanded/collapsed state. */
  readonly onToggleExpand: ToggleExpandHandler;

  /** Optional header element id used for aria-labelledby relationships. */
  readonly headerId?: string;

  /** Optional content region id used for aria-controls relationships. */
  readonly contentId?: string;
}

/** The `Collapse` content component properties. */
export interface CollapseContentProps {
  /** Additional class names for variant-specific content styling. */
  readonly className: string;

  /** Whether content should render in expanded state styling. */
  readonly expanded: boolean;

  /** The collapse body content. */
  readonly children?: ReactNode;
}

/** The `CustomCollapse` component properties. */
export interface CustomCollapseProps extends BaseProps {
  /** The collapse header title. */
  readonly title?: ReactNode;

  /** Body content rendered within the collapse content region. */
  readonly children?: ReactNode;

  /** Header renderer used to display and toggle the collapse trigger. */
  readonly headerComponent: ComponentType<CollapseHeaderProps>;

  /** Content renderer used for variant-specific content layout/styling. */
  readonly contentComponent: ComponentType<CollapseContentProps>;

  /** Which side to align the caret; defaults to `right`. */
  readonly alignCaret?: CaretAlignment;

  /** Controlled expanded state; defaults to `false`. */
  readonly expanded?: boolean;

  /** If true, keeps hidden content mounted while collapsed. */
  readonly keepHiddenContentInDOM?: boolean;

  /** Called when expanded state changes. */
  readonly onToggleExpand?: ToggleExpandHandler;
}

const useResolvedCollapseStyles = createComponentStyleHook<BaseProps>({
  legacyStyles,
  tokenStyles,
  componentTokens: "collapse",
});

const renderHeaderButtonBase = (
  styles: typeof tokenStyles,
  {
    title,
    expanded,
    alignCaret,
    onToggleExpand,
    headerId,
    contentId,
    showBorderBottom,
    unpadded,
  }: CollapseHeaderProps & { showBorderBottom: boolean; unpadded: boolean },
) => {
  /* `headerId` and `contentId` are always populated by InternalCustomCollapse
     (derived from `useId()`); no slug fallback needed. */
  const headerClickHandler = (ev: MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    onToggleExpand?.(!expanded, expanded);
  };

  return (
    <button
      id={headerId}
      type="button"
      aria-expanded={expanded}
      aria-controls={contentId}
      onClick={headerClickHandler}
      className={classNames(styles.HeaderButton, {
        [styles.HeaderButtonExpanded]: expanded && showBorderBottom,
        [styles.HeaderButtonUnpadded]: unpadded,
      })}
    >
      <span className="collapse-header">
        <Shelf spacing="mini">
          {alignCaret === "left" && (
            <span className={styles.Caret}>
              <Icon name={expanded ? "chevron-down" : "chevron-right"} size="regular" />
            </span>
          )}
          <span className={styles.HeaderContent}>
            <Body>
              <span className={classNames(styles.Title, "collapse-title")}>
                <Semibold>{title}</Semibold>
              </span>
            </Body>
          </span>
          {alignCaret === "right" && (
            <span className={styles.Caret}>
              <Icon name={expanded ? "chevron-up" : "chevron-down"} size="regular" />
            </span>
          )}
        </Shelf>
      </span>
    </button>
  );
};

/** The common icon collapse header. */
const createCommonIconCollapseHeader = (styles: typeof tokenStyles, options?: { showBorderBottom?: boolean; unpadded?: boolean }) => {
  const { showBorderBottom = true, unpadded = false } = options ?? {};

  return (props: CollapseHeaderProps) => renderHeaderButtonBase(styles, { ...props, showBorderBottom, unpadded });
};

/** The common checkbox collapse header. */
const CommonCheckboxCollapseHeader = (props: CollapseHeaderProps) => {
  const { title, expanded, onToggleExpand } = props;

  const checkboxChangeHandler = useCallback(
    (value: CheckboxValue, previousValue: CheckboxValue) => {
      if (value === "indeterminate" || value === null) return;
      if (previousValue === "indeterminate" || previousValue === null) return;

      onToggleExpand?.(value, previousValue);
    },
    [onToggleExpand],
  );

  return (
    <div className="collapse-header">
      <Checkbox value={expanded} hideInstructions hideLabel label={title} onChange={checkboxChangeHandler} />
    </div>
  );
};

/** The common content component. */
const renderCommonContent = (styles: typeof tokenStyles, { className, expanded, children }: CollapseContentProps) => {
  return (
    <div
      className={classNames(className, styles.CommonContent, {
        [styles.ContentExpanded]: expanded,
        [styles.ContentCollapsed]: !expanded,
      })}
    >
      <Panel>{children}</Panel>
    </div>
  );
};

const createCommonContentComponent = (styles: typeof tokenStyles, variantClassName: string, options?: { unpadded?: boolean }) => {
  const { unpadded = false } = options ?? {};

  return ({ className, expanded, children }: CollapseContentProps) =>
    renderCommonContent(styles, {
      className: classNames(className, variantClassName, { [styles.UnpaddedContent]: unpadded }),
      expanded,
      children,
    });
};

const createContainedContentComponent = (styles: typeof tokenStyles) => createCommonContentComponent(styles, styles.ContainedContent);

const createUnderlinedContentComponent = (styles: typeof tokenStyles) => createCommonContentComponent(styles, styles.UnderlinedContent);

const createUnpaddedUnderlinedContentComponent = (styles: typeof tokenStyles) =>
  createCommonContentComponent(styles, styles.UnderlinedContent, { unpadded: true });

const createBlankContentComponent = (styles: typeof tokenStyles) => createCommonContentComponent(styles, styles.BlankContent);

const createUnpaddedBlankContentComponent = (styles: typeof tokenStyles) =>
  createCommonContentComponent(styles, styles.BlankContent, { unpadded: true });

const createPanelCollapseContentComponent = (styles: typeof tokenStyles) => {
  return ({ className, expanded, children }: CollapseContentProps) => (
    <div
      className={classNames(className, styles.PanelContent, {
        [styles.ContentExpanded]: expanded,
        [styles.ContentCollapsed]: !expanded,
      })}
    >
      {children}
    </div>
  );
};

/** The collapse properties for variants. */
export interface VariantCollapseProps extends Omit<CustomCollapseProps, "headerComponent" | "contentComponent"> {
  /** Which caret style to render for the variant; defaults to `icon`. */
  readonly caret?: CaretType;
}

/** The collapse variant component type. */
export type CollapseVariantComponentType<P extends VariantCollapseProps> = ComponentType<P> & {
  CollapseGroupContainer?: ComponentType<StackProps>;
};

type InternalCustomCollapseProps = CustomCollapseProps & {
  readonly variantClassName?: string;
  /* Required: variants and `Collapse.Custom` resolve styles once and pass them
     in. This avoids running the style-resolution hook a second time inside
     InternalCustomCollapse on every render of every variant. */
  readonly resolvedStyles: ReturnType<typeof useResolvedCollapseStyles>;
};

/** A component which collapses and expands its child content. */
const InternalCustomCollapse = (props: InternalCustomCollapseProps) => {
  const {
    expanded = false,
    headerComponent: HeaderComponent,
    contentComponent: ContentComponent,
    alignCaret = "right",
    title,
    children,
    keepHiddenContentInDOM = false,
    onToggleExpand,
    variantClassName,
    resolvedStyles,
  } = props;

  const { styles, mode, StyleModeBoundary } = resolvedStyles;

  const instanceId = useId();
  const contentId = `collapse-content-${instanceId}`;
  const headerId = `collapse-header-${instanceId}`;

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [styles.Collapse, variantClassName, "collapse", { collapsed: !expanded, expanded }],
    testIdPrefix: "collapse",
  });

  const toggleExpand = useCallback(
    (nextExpanded: boolean, previous: boolean) => {
      onToggleExpand?.(nextExpanded, previous);
    },
    [onToggleExpand],
  );

  return (
    <StyleModeBoundary mode={mode}>
      <div {...baseHtmlProps}>
        <Stack spacing="none">
          <HeaderComponent
            title={title}
            expanded={expanded}
            alignCaret={alignCaret}
            onToggleExpand={toggleExpand}
            headerId={headerId}
            contentId={contentId}
          />
          <ContentComponent className="collapse-content" expanded={expanded}>
            <div className="collapse-content-container" id={contentId} role="region" aria-labelledby={headerId} hidden={!expanded}>
              {(keepHiddenContentInDOM || expanded) && children}
            </div>
          </ContentComponent>
        </Stack>
      </div>
    </StyleModeBoundary>
  );
};

/** Public custom collapse variant that intentionally hides internal style-plumbing props. */
const CustomCollapse = (props: CustomCollapseProps) => {
  const resolvedStyles = useResolvedCollapseStyles(props);
  return <InternalCustomCollapse {...props} resolvedStyles={resolvedStyles} />;
};

/** The `Collapse` component properties. */
export interface CollapseProps extends Omit<CustomCollapseProps, "headerComponent" | "contentComponent"> {
  /** Predefined collapse appearance variant; defaults to `contained`. */
  readonly appearance?: CollapseAppearance;
}

/** A component which collapses and expands its child content. */
export const Collapse = (props: CollapseProps) => {
  const { appearance = "contained", children, ...rest } = props;

  switch (appearance) {
    case "underlined":
      return (
        <Collapse.Underlined {...rest} caret="icon">
          {children}
        </Collapse.Underlined>
      );
    case "underlined-no-indent":
      return (
        <Collapse.Underlined.Unpadded {...rest} caret="icon">
          {children}
        </Collapse.Underlined.Unpadded>
      );
    case "blank":
      return (
        <Collapse.Blank {...rest} caret="icon">
          {children}
        </Collapse.Blank>
      );
    case "blank-no-indent":
      return (
        <Collapse.Blank.Unpadded {...rest} caret="icon">
          {children}
        </Collapse.Blank.Unpadded>
      );
    case "checkbox":
      return (
        <Collapse.Contained {...rest} caret="checkbox">
          {children}
        </Collapse.Contained>
      );
    case "contained":
    default:
      return (
        <Collapse.Contained {...rest} caret="icon">
          {children}
        </Collapse.Contained>
      );
  }
};

/** A contained variant of the `Collapse` component. */
Collapse.Contained = ((props) => {
  const resolvedStyles = useResolvedCollapseStyles(props);
  const { styles } = resolvedStyles;
  const { caret, children, className, ...rest } = props;
  const header = useMemo(
    () => (caret === "checkbox" ? CommonCheckboxCollapseHeader : createCommonIconCollapseHeader(styles)),
    [caret, styles],
  );
  const contentComponent = useMemo(() => createContainedContentComponent(styles), [styles]);

  return (
    <InternalCustomCollapse
      {...rest}
      className={className}
      variantClassName={styles.Contained}
      resolvedStyles={resolvedStyles}
      headerComponent={header}
      contentComponent={contentComponent}
    >
      {children}
    </InternalCustomCollapse>
  );
}) as CollapseVariantComponentType<VariantCollapseProps>;

const Underlined = ((props) => {
  const resolvedStyles = useResolvedCollapseStyles(props);
  const { styles } = resolvedStyles;
  const { caret, children, className, ...rest } = props;

  const header = useMemo(
    () => (caret === "checkbox" ? CommonCheckboxCollapseHeader : createCommonIconCollapseHeader(styles, { showBorderBottom: false })),
    [caret, styles],
  );
  const contentComponent = useMemo(() => createUnderlinedContentComponent(styles), [styles]);

  return (
    <InternalCustomCollapse
      {...rest}
      className={className}
      variantClassName={styles.Underlined}
      resolvedStyles={resolvedStyles}
      headerComponent={header}
      contentComponent={contentComponent}
    >
      {children}
    </InternalCustomCollapse>
  );
}) as CollapseVariantComponentType<VariantCollapseProps> & {
  Unpadded: CollapseVariantComponentType<VariantCollapseProps>;
};

Underlined.Unpadded = (props) => {
  const resolvedStyles = useResolvedCollapseStyles(props);
  const { styles } = resolvedStyles;
  const { caret, children, className, ...rest } = props;

  const header = useMemo(
    () =>
      caret === "checkbox"
        ? CommonCheckboxCollapseHeader
        : createCommonIconCollapseHeader(styles, { showBorderBottom: false, unpadded: true }),
    [caret, styles],
  );
  const contentComponent = useMemo(() => createUnpaddedUnderlinedContentComponent(styles), [styles]);

  return (
    <InternalCustomCollapse
      {...rest}
      className={className}
      variantClassName={classNames(styles.Underlined, styles.Unpadded)}
      resolvedStyles={resolvedStyles}
      headerComponent={header}
      contentComponent={contentComponent}
    >
      {children}
    </InternalCustomCollapse>
  );
};

Collapse.Underlined = Underlined;

const Blank = ((props) => {
  const resolvedStyles = useResolvedCollapseStyles(props);
  const { styles } = resolvedStyles;
  const { caret, children, className, ...rest } = props;

  const header = useMemo(
    () => (caret === "checkbox" ? CommonCheckboxCollapseHeader : createCommonIconCollapseHeader(styles, { showBorderBottom: false })),
    [caret, styles],
  );
  const contentComponent = useMemo(() => createBlankContentComponent(styles), [styles]);

  return (
    <InternalCustomCollapse
      {...rest}
      className={className}
      variantClassName={styles.Blank}
      resolvedStyles={resolvedStyles}
      headerComponent={header}
      contentComponent={contentComponent}
    >
      {children}
    </InternalCustomCollapse>
  );
}) as CollapseVariantComponentType<VariantCollapseProps> & {
  Unpadded: CollapseVariantComponentType<VariantCollapseProps>;
};

Blank.Unpadded = (props) => {
  const resolvedStyles = useResolvedCollapseStyles(props);
  const { styles } = resolvedStyles;
  const { caret, children, className, ...rest } = props;

  const header = useMemo(
    () =>
      caret === "checkbox"
        ? CommonCheckboxCollapseHeader
        : createCommonIconCollapseHeader(styles, { showBorderBottom: false, unpadded: true }),
    [caret, styles],
  );
  const contentComponent = useMemo(() => createUnpaddedBlankContentComponent(styles), [styles]);

  return (
    <InternalCustomCollapse
      {...rest}
      className={className}
      variantClassName={classNames(styles.Blank, styles.Unpadded)}
      resolvedStyles={resolvedStyles}
      headerComponent={header}
      contentComponent={contentComponent}
    >
      {children}
    </InternalCustomCollapse>
  );
};

Collapse.Blank = Blank;

/** A panel variant of the `Collapse` component. */
const CollapsePanel = ((props) => {
  const resolvedStyles = useResolvedCollapseStyles(props);
  const { styles } = resolvedStyles;
  const { className, caret, children, ...rest } = props;

  const header = useMemo(
    () => (caret === "checkbox" ? CommonCheckboxCollapseHeader : createCommonIconCollapseHeader(styles)),
    [caret, styles],
  );
  const contentComponent = useMemo(() => createPanelCollapseContentComponent(styles), [styles]);

  return (
    <div className={classNames(styles.PanelRoot, className)}>
      <Panel appearance="primary">
        <InternalCustomCollapse
          {...rest}
          variantClassName={styles.Panel}
          resolvedStyles={resolvedStyles}
          headerComponent={header}
          contentComponent={contentComponent}
        >
          {children}
        </InternalCustomCollapse>
      </Panel>
    </div>
  );
}) as CollapseVariantComponentType<VariantCollapseProps>;

const CollapsePanelCollapseGroupContainer = (props: StackProps) => {
  const { className, spacing, children, ...rest } = props;
  const parentPanelLevel = usePanelLevel();

  const showShadow = spacing === "none" && parentPanelLevel === "none";

  return (
    <div className={classNames(tokenStyles.PanelGroupContainer, { [tokenStyles.PanelGroupContainerShadow]: showShadow }, className)}>
      <Stack {...rest} spacing={spacing}>
        {children}
      </Stack>
    </div>
  );
};

CollapsePanel.CollapseGroupContainer = CollapsePanelCollapseGroupContainer;

Collapse.Panel = CollapsePanel;

/** A customizable version of the Collapse (will eventually also be the same as Collapse). */
Collapse.Custom = CustomCollapse;
